import { useState } from 'react';

/**
 * Custom hook for managing Incident Location state
 */
const useIncidentLocation = () => {
  const getDefaultIncidentLocation = () => ({
    provStateTerritory: '',
    countyMunicipality: '',
    city: '',
    unitOrOther: '',
    jurisdiction: '',
    incidentLocationOwnership: '',
    latitude: '',
    longitude: '',
    datum: 'WGS84',
    utmCoordinates: '',
    legalDescription: '',
    shortLocationDescription: '',
    geospatialDataAvailable: false
  });

  const [incidentLocations, setIncidentLocations] = useState([]);
  const [currentIncidentLocation, setCurrentIncidentLocation] = useState(getDefaultIncidentLocation());
  const [editingIncidentLocation, setEditingIncidentLocation] = useState(null);
  const [locationErrors, setLocationErrors] = useState({});

  const addIncidentLocation = () => {
    if (!currentIncidentLocation.provStateTerritory) {
      setLocationErrors({ provStateTerritory: 'Province/State/Territory is required' });
      return;
    }
    
    if (editingIncidentLocation) {
      setIncidentLocations(prev => prev.map(loc => 
        loc.id === editingIncidentLocation.id 
          ? { ...currentIncidentLocation, id: loc.id, timestamp: loc.timestamp, updatedAt: new Date().toISOString() }
          : loc
      ));
      setEditingIncidentLocation(null);
    } else {
      const newLocation = {
        ...currentIncidentLocation,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      setIncidentLocations(prev => [...prev, newLocation]);
    }
    
    setCurrentIncidentLocation(getDefaultIncidentLocation());
    setLocationErrors({});
  };

  const editIncidentLocation = (location) => {
    setCurrentIncidentLocation({
      provStateTerritory: location.provStateTerritory || '',
      countyMunicipality: location.countyMunicipality || '',
      city: location.city || '',
      unitOrOther: location.unitOrOther || '',
      jurisdiction: location.jurisdiction || '',
      incidentLocationOwnership: location.incidentLocationOwnership || '',
      latitude: location.latitude || '',
      longitude: location.longitude || '',
      datum: location.datum || 'WGS84',
      utmCoordinates: location.utmCoordinates || '',
      legalDescription: location.legalDescription || '',
      shortLocationDescription: location.shortLocationDescription || '',
      geospatialDataAvailable: location.geospatialDataAvailable || false
    });
    setEditingIncidentLocation(location);
  };

  const cancelEditIncidentLocation = () => {
    setCurrentIncidentLocation(getDefaultIncidentLocation());
    setLocationErrors({});
    setEditingIncidentLocation(null);
  };

  const deleteIncidentLocation = (locationId) => {
    if (window.confirm('Are you sure you want to delete this incident location?')) {
      setIncidentLocations(prev => prev.filter(loc => loc.id !== locationId));
    }
  };

  return {
    // State
    incidentLocations,
    setIncidentLocations,
    currentIncidentLocation,
    setCurrentIncidentLocation,
    editingIncidentLocation,
    setEditingIncidentLocation,
    locationErrors,
    setLocationErrors,
    // Functions
    addIncidentLocation,
    editIncidentLocation,
    cancelEditIncidentLocation,
    deleteIncidentLocation,
    getDefaultIncidentLocation
  };
};

export default useIncidentLocation;
